import {
    actionTypesForbiddingReference,
    actionTypesRequiringReference,
    CurationActionTypeEnum,
    CurationAgentActionInput,
    CurationAgentActionResponse,
    fromApiInsightType,
    toApiInsightType
} from '../../api/agents/models/curation-agent.api';
import { maxInsightKeywords } from '../../api/agents/models/curation-agent.api';
import { MentionInsightTypes } from '../../Common/Mentions/enums/mention-insight-types.enum';

/**
 * The insights the service accepts keywords for - the two that read a context rather than working
 * from the mention alone. Sending them on any other type is rejected.
 */
export const contextReadingInsightTypes: MentionInsightTypes[] = [
    MentionInsightTypes.CONTEXTUAL_ANALYSIS,
    MentionInsightTypes.SENTIMENT_OPINION
];

/** Badge icons, per the Action Badges Icons design. Insight and RSS have no badge in that set. */
const actionIcons: Partial<Record<CurationActionTypeEnum, string>> = {
    [CurationActionTypeEnum.saveToAccount]: 'fa-solid fa-floppy-disk',
    [CurationActionTypeEnum.saveToFolder]: 'fa-solid fa-folder',
    [CurationActionTypeEnum.saveToTag]: 'fa-solid fa-tag',
    [CurationActionTypeEnum.deleteFromAccount]: 'fa-solid fa-circle-x',
    [CurationActionTypeEnum.removeFromFolder]: 'fa-solid fa-folder-xmark',
    [CurationActionTypeEnum.removeFromTag]: 'fa-kit fa-agpr-remove-tag',
    [CurationActionTypeEnum.generateInsight]: 'fa-kit fa-agpr-insights-solid',
    [CurationActionTypeEnum.sendToRss]: 'fa-solid fa-rss'
};

/**
 * One action the agent runs against a curated mention. An action carries every target it was given,
 * so picking three folders is one action holding three ids rather than three actions.
 */
export class CurationAgentActionModel {
    /** Set once saved. Passing it back on update keeps the row rather than replacing it */
    id: number = null;
    type: CurationActionTypeEnum = null;
    /** The folders/tags/feeds this action targets, or empty for the no-target types */
    referenceIds: string[] = [];

    /**
     * Generate Insight only: the prebuilt insight to generate. Set instead of {@link referenceIds},
     * which on that type carries a single custom prompt id - the service rejects more than one.
     */
    insightType: MentionInsightTypes = null;
    /** Generate Insight only: the language the insight is written in, eg. en-US */
    language: string = null;

    /**
     * Generate Insight only, and only on the insight types that read one: the keywords the insight
     * is written about - what the panel calls the context.
     */
    keywords: string[] = [];

    /**
     * Display names of whatever `referenceIds` point at, in the same order, resolved by whoever
     * built the action. Presentation only, so they are never sent.
     */
    referenceNames: string[] = [];

    // FE Only for when we automatically add the Save to Account Action
    autoAdded: boolean = false;

    constructor(apiModel?: CurationAgentActionResponse) {
        if (!apiModel) {
            return;
        }

        this.id = apiModel.id;
        this.type = apiModel.actionType;
        this.referenceIds = apiModel.actionReferenceIds ?? [];
        this.insightType = fromApiInsightType(apiModel.insightType) as MentionInsightTypes;
        this.language = apiModel.languageTag ?? null;
        this.keywords = apiModel.keywords ?? [];
    }

    /** The one target the single target types carry - an RSS feed, or a custom insight's prompt */
    get referenceId(): string {
        return this.referenceIds[0] ?? null;
    }

    /** Every target paired with the name it renders under, so each one can be listed and linked */
    get targets(): { id: string; name: string }[] {
        return this.referenceIds.map((id, index) => ({ id, name: this.referenceNames[index] ?? id }));
    }

    /** Whether this action's insight is one of the two the service takes keywords for */
    get readsContext(): boolean {
        return this.type === CurationActionTypeEnum.generateInsight
            && contextReadingInsightTypes.includes(this.insightType);
    }

    static forType(
        type: CurationActionTypeEnum,
        referenceIds: string[] = [],
        referenceNames: string[] = []
    ): CurationAgentActionModel {
        const action = new CurationAgentActionModel();
        action.type = type;
        action.referenceIds = [...referenceIds];
        action.referenceNames = [...referenceNames];

        return action;
    }

    /** Generate Insight running one of the platform's prebuilt insights rather than a custom prompt */
    static forInsightType(
        insightType: MentionInsightTypes,
        language: string,
        insightName: string,
        keywords: string[] = []
    ): CurationAgentActionModel {
        const action = new CurationAgentActionModel();
        action.type = CurationActionTypeEnum.generateInsight;
        action.insightType = insightType;
        action.language = language;
        action.referenceNames = [insightName];
        action.keywords = [...(keywords ?? [])].slice(0, maxInsightKeywords);

        return action;
    }

    get icon(): string {
        return actionIcons[this.type] ?? 'fa-regular fa-duotone fa-gear';
    }

    /** Right hand side of an added action card - the folder or tag name, and so on */
    get detailLabel(): string {
        let label;

        if (this.type === CurationActionTypeEnum.generateInsight && !!this.insightType) {
            label = `Mention.workbench.insights.system.${this.insightType}`;
        } else {
            // Everything the action targets, so a three folder action reads as all three names
            label = this.targets.map(target => target.name).join(', ');
        }
        return label;
    }

    get needsReference(): boolean {
        return actionTypesRequiringReference.includes(this.type);
    }

    get forbidsReference(): boolean {
        return actionTypesForbiddingReference.includes(this.type);
    }

    /** Whether everything the selected type needs has been chosen */
    isValid(): boolean {
        if (!this.type) {
            return false;
        }

        // Generate Insight is satisfied by either path - a prebuilt insight or a custom prompt
        if (this.type === CurationActionTypeEnum.generateInsight) {
            return !!this.insightType || !!this.referenceIds.length;
        }

        return this.needsReference ? !!this.referenceIds.length : true;
    }

    toInput(): CurationAgentActionInput {
        const input: CurationAgentActionInput = { actionType: this.type };

        if (this.id) {
            input.id = this.id;
        }

        // Exactly one of the two on Generate Insight: a prebuilt insight, or a custom prompt id
        if (this.insightType) {
            input.insightType = toApiInsightType(this.insightType);
        } else if (!this.forbidsReference && this.referenceIds.length) {
            // The service rejects a reference on the types that act on the mention itself
            input.actionReferenceIds = [...this.referenceIds];
        }

        if (this.language) {
            input.languageTag = this.language;
        }

        // Only the context reading insight types accept them; the service rejects them elsewhere
        if (this.keywords?.length && this.readsContext) {
            input.keywords = this.keywords;
        }

        return input;
    }

    clone(): CurationAgentActionModel {
        const clone = new CurationAgentActionModel();
        clone.id = this.id;
        clone.type = this.type;
        clone.referenceIds = [...this.referenceIds];
        clone.referenceNames = [...this.referenceNames];
        clone.insightType = this.insightType;
        clone.language = this.language;
        clone.keywords = [...(this.keywords ?? [])];

        return clone;
    }
}
